import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState } from "../../state";
import { resolveGlobalPositions } from "../render/resolveGlobalPositions";

import { updateEntities } from "./updateEntities";
import { updateSceneConfig } from "./updateSceneConfig";

/**
 * The panel's entity registry, falling back to one derived from its components.
 *
 * The registry postdates the panel, so a workspace saved earlier has components
 * with no registry beside them. Deriving a flat, parentless registry makes such a
 * panel render exactly as it did before the hierarchy existed, rather than as a
 * panel with no entities.
 */
function entityRegistryOf(panelState: {
  config?: schema.PanelConfig;
  data: schema.PanelData;
}): schema.EntityRegistry {
  const declared = panelState.config?.entities;
  if (declared && Object.keys(declared).length > 0) {
    return declared;
  }
  return Object.fromEntries(
    Object.keys(panelState.data.entities).map((id) => [id, {}]),
  );
}

/**
 * Synchronizes the babylon state with the incoming panelState.
 */
export function onSetState(
  state: BabylonState,
  panelState: { config?: schema.PanelConfig; data: schema.PanelData },
) {
  updateEntities(state, panelState.data, entityRegistryOf(panelState));
  // `buildOrbitCamera` seeds its focus point from the tracked entity's
  // *absolute* position, and this runs before the first render pass, so the
  // globals have to be accumulated here or a camera built on the first state
  // update would frame an unresolved (0,0,0).
  resolveGlobalPositions(state);
  // The scene config includes references to entity IDs,
  // so make sure the entities are updated first.
  updateSceneConfig(state, panelState.config);
}
